import { gql } from '@apollo/client'

const MONEY_FIELDS = 'value display'

const ATTACHMENT_FIELDS = 'id filename contentType url thumbnailUrl'

export const LOAN_AGREEMENT_FIELDS = gql`
  fragment LoanAgreementFields on AdminLoanAgreement {
    id
    borrower
    issueDate
    dueDate
    amount { ${MONEY_FIELDS} }
    interestRate
    interestPeriod
    withHoldingTaxRate
    interestPerDay { ${MONEY_FIELDS} }
    totalInterest { ${MONEY_FIELDS} }
    withHoldingTax { ${MONEY_FIELDS} }
    totalAmount { ${MONEY_FIELDS} }
    principleStatus
    interestStatus
    principlePaidDate
    interestPaidDate
    paymentNote
    updatedAt
    signedDocuments { ${ATTACHMENT_FIELDS} }
    slips { ${ATTACHMENT_FIELDS} }
  }
`

export const LOAN_AGREEMENTS_QUERY = gql`
  query LoanAgreements(
    $page: Int
    $per: Int
    $borrowerQuery: String
    $principleStatus: LoanAgreementPrincipleStatusEnum
    $interestStatus: LoanAgreementInterestStatusEnum
    $sort: LoanAgreementSortEnum
  ) {
    loanAgreements(
      page: $page
      per: $per
      borrowerQuery: $borrowerQuery
      principleStatus: $principleStatus
      interestStatus: $interestStatus
      sort: $sort
    ) {
      totalCount
      totalPages
      summary {
        principlePending { ${MONEY_FIELDS} }
        principlePaid { ${MONEY_FIELDS} }
        principleTotal { ${MONEY_FIELDS} }
        interestPending { ${MONEY_FIELDS} }
        interestPaid { ${MONEY_FIELDS} }
        interestTotal { ${MONEY_FIELDS} }
        pendingAmount { ${MONEY_FIELDS} }
        paidAmount { ${MONEY_FIELDS} }
        totalAmount { ${MONEY_FIELDS} }
      }
      loanAgreements {
        ...LoanAgreementFields
      }
    }
  }
  ${LOAN_AGREEMENT_FIELDS}
`

export const LOAN_AGREEMENT_QUERY = gql`
  query LoanAgreement($id: ID!) {
    loanAgreement(id: $id) {
      ...LoanAgreementFields
    }
  }
  ${LOAN_AGREEMENT_FIELDS}
`

export const UPSERT_LOAN_AGREEMENT_MUTATION = gql`
  mutation UpsertLoanAgreement(
    $id: ID
    $borrower: String
    $issueDate: ISO8601Date
    $amount: Float
    $interestPeriod: Float
    $interestRate: Float
    $withHoldingTaxRate: Float
    $principleStatus: LoanAgreementPrincipleStatusEnum
    $interestStatus: LoanAgreementInterestStatusEnum
    $principlePaidDate: ISO8601Date
    $interestPaidDate: ISO8601Date
    $paymentNote: String
    $signedDocumentBlobIds: [ID!]
    $slipBlobIds: [ID!]
  ) {
    upsertLoanAgreement(
      id: $id
      borrower: $borrower
      issueDate: $issueDate
      amount: $amount
      interestPeriod: $interestPeriod
      interestRate: $interestRate
      withHoldingTaxRate: $withHoldingTaxRate
      principleStatus: $principleStatus
      interestStatus: $interestStatus
      principlePaidDate: $principlePaidDate
      interestPaidDate: $interestPaidDate
      paymentNote: $paymentNote
      signedDocumentBlobIds: $signedDocumentBlobIds
      slipBlobIds: $slipBlobIds
    ) {
      loanAgreement {
        ...LoanAgreementFields
      }
      errors {
        key
        message
      }
    }
  }
  ${LOAN_AGREEMENT_FIELDS}
`

export const DELETE_LOAN_AGREEMENT_ATTACHMENT_MUTATION = gql`
  mutation DeleteLoanAgreementAttachment($id: ID!, $attachmentId: ID!) {
    deleteLoanAgreementAttachment(id: $id, attachmentId: $attachmentId) {
      deletedAttachmentId
      loanAgreement {
        ...LoanAgreementFields
      }
    }
  }
  ${LOAN_AGREEMENT_FIELDS}
`

export const DELETE_ACTIVE_STORAGE_BLOB_MUTATION = gql`
  mutation DeleteLoanAgreementUploadBlob($blobIds: [ID!]) {
    deleteActiveStorageBlob(blobIds: $blobIds) {
      deleteBlobIds
    }
  }
`

export const PRINCIPLE_STATUSES = ['principle_pending', 'principle_paid']

export const INTEREST_STATUSES = ['interest_pending', 'interest_paid']

export const STATUS_COLORS = {
  principle_pending: 'gold',
  principle_paid: 'green',
  interest_pending: 'gold',
  interest_paid: 'green'
}

export const SORTS = {
  issueDate: { ascend: 'ISSUE_DATE_ASC', descend: 'ISSUE_DATE_DESC' },
  updatedAt: { ascend: 'UPDATED_AT_ASC', descend: 'UPDATED_AT_DESC' }
}

export const DEFAULT_SORT = 'ISSUE_DATE_DESC'

export const DEFAULT_WITH_HOLDING_TAX_RATE = 0.03

export const LOAN_AGREEMENT_ROLES = ['super_admin', 'loan_agreement']

export const formatMutationErrors = (errors = []) => errors.map(({ message }) => message).join(', ')
